import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import './Signup.css';

interface SignupFormData {
  email: string;
  company_name: string;
  country: string;
  phone?: string;
}

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SignupFormData>({
    email: '',
    company_name: '',
    country: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [agreeTos, setAgreeTos] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Company name validation
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }

    // Country validation
    if (!formData.country) {
      newErrors.country = 'Please select a country';
    }

    // Terms of service
    if (!agreeTos) {
      newErrors.tos = 'You must agree to the Terms of Service';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Send email verification instead of creating account directly
      const result = await apiService.sendVerificationEmail({
        email: formData.email,
        company_name: formData.company_name,
        country: formData.country,
        phone: formData.phone
      });

      if (result.status === 'success') {
        // Redirect to verification pending page
        navigate(`/verify-email-pending?email=${encodeURIComponent(formData.email)}`);
      } else {
        throw new Error(result.detail || 'Failed to send verification email');
      }

    } catch (error: any) {
      console.error('Signup error:', error);
      
      let errorMessage = 'Failed to create account. Please try again.';
      
      // Handle different error formats
      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Check for specific error types
      if (errorMessage.includes('already exists')) {
        setErrors({ email: 'An account with this email already exists' });
      } else if (errorMessage.includes('Too many signup attempts')) {
        setErrors({ general: 'Too many signup attempts. Please try again in 15 minutes.' });
      } else if (errorMessage.includes('already sent')) {
        setErrors({ general: 'Verification email already sent. Check your inbox or wait before requesting another.' });
      } else {
        setErrors({ general: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const countries = [
    'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic',
    'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece',
    'Hungary', 'Ireland', 'Italy', 'Latvia', 'Lithuania', 'Luxembourg',
    'Malta', 'Netherlands', 'Poland', 'Portugal', 'Romania', 'Slovakia',
    'Slovenia', 'Spain', 'Sweden', 'United Kingdom', 'Other'
  ];

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-header">
          <h1>Start Your EUDR Compliance Journey</h1>
        </div>

        <div className="signup-benefits">
          <div className="benefit-item">
            <span className="benefit-icon">⚡</span>
            <span>Try Before You Buy</span>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">📊</span>
            <span>Instant Analysis Report</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="signup-form">
          {errors.general && (
            <div className="error-message general">
              {errors.general}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Business Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
              placeholder="your@company.com"
              required
            />
            {errors.email && <div className="error-text">{errors.email}</div>}
            <div className="field-help">This will be your Customer ID for login</div>
          </div>

          <div className="form-group">
            <label htmlFor="company_name">Company Name *</label>
            <input
              type="text"
              id="company_name"
              name="company_name"
              value={formData.company_name}
              onChange={handleInputChange}
              className={errors.company_name ? 'error' : ''}
              placeholder="Your Company Ltd."
              required
            />
            {errors.company_name && <div className="error-text">{errors.company_name}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="country">Country *</label>
            <select
              id="country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              className={errors.country ? 'error' : ''}
              required
            >
              <option value="">Select your country</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
            {errors.country && <div className="error-text">{errors.country}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number (Optional)</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="+1 234 567 8900"
            />
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={agreeTos}
                onChange={(e) => setAgreeTos(e.target.checked)}
                className={errors.tos ? 'error' : ''}
              />
              I agree to the <a href="/terms" target="_blank">Terms of Service</a> and <a href="/privacy" target="_blank">Privacy Policy</a>
            </label>
            {errors.tos && <div className="error-text">{errors.tos}</div>}
          </div>

          <button 
            type="submit" 
            className="btn primary large"
            disabled={loading}
          >
            {loading ? 'Sending Verification...' : 'Send Verification Email'}
          </button>

          <div className="signup-footer">
            <p>Already have an account? <a href="/upload">Sign in here</a></p>
            <div className="security-note">
              <span className="security-icon">🔒</span>
              Your data is encrypted and secure
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signup;